import * as THREE from 'three';
import {
  Scene,
  Matrix4,
  CanvasTexture,
  MeshLambertMaterial,
  Object3D,
  BufferGeometry,
} from 'three';
import { type FC, useCallback, useRef, useMemo, useEffect, useState } from 'react';
import { useThree, createPortal, type ThreeEvent, addAfterEffect } from '@react-three/fiber';
import { useCamera } from '@react-three/drei';
import { ViewerLayer } from './ViewerLayer';
import { ViewCubeCompass, type ViewCubeCompassProps } from './ViewCubeCompass';
import { removeNullTyping } from './Util';

export interface ViewCubeProps {
  cubeSize: number;
  fontSize: number;
  colors: {
    foreground: string;
    background: string;
    hoverForeground: string;
    hoverBackground: string;
  };
  position?: [x: number, y: number, z: number];
  compass: ViewCubeCompassProps;
}

// eslint-disable-next-line react-refresh/only-export-components
export const defaultViewCubeProps = {
  cubeSize: 60,
  fontSize: 60,
  colors: {
    foreground: '#000',
    background: '#CCC',
    hoverForeground: '#000',
    hoverBackground: '#44AAFF',
  },
  compass: {
    ringColor: '#888',
    letterStyle: { fontSize: 16, color: '#AAA', strokeColor: '#111', strokeWidth: 0.1 },
  },
};

export interface ViewCubeExtendedProps extends ViewCubeProps {
  northRotation: number;
  moveCameraToViewDirection: (direction: [x: number, y: number, z: number]) => void;
}

function nearestPowerOf2(n: number) {
  return 1 << (31 - Math.clz32(n));
}

function calcFaceCanvasParameters(faceWidth: number, text: string, fontSize: number) {
  const context = document.createElement('canvas').getContext('2d')!;
  const font = `${fontSize}px bold sans-serif`;
  context.font = font;

  const borderSize = 2;
  const doubleBorderSize = borderSize * 2;

  // measure how long the name will be
  const textWidth = context.measureText(text).width * 1.1;
  const width = 2 * nearestPowerOf2(faceWidth + doubleBorderSize);
  const scaleFactor = Math.min(1, width / textWidth);

  return { font, width, scaleFactor };
}

function makeFaceCanvas(
  faceCanvasParameters: {
    font: string;
    width: number;
    scaleFactor: number;
  },
  text: string,
  color: string,
  backgroundColor: string
) {
  const { font, width, scaleFactor } = faceCanvasParameters;

  const context = document.createElement('canvas').getContext('2d')!;

  const height = width;
  context.canvas.width = width;
  context.canvas.height = height;

  // need to set font again after resizing canvas
  context.font = font;
  context.textBaseline = 'middle';
  context.textAlign = 'center';

  context.fillStyle = backgroundColor;
  context.fillRect(0, 0, width, height);
  context.strokeStyle = color;
  context.rect(0, 0, width, height);
  context.stroke();

  // scale to fit but don't stretch
  context.translate(width / 2, height / 2);
  context.scale(scaleFactor, scaleFactor * 1.2);
  context.fillStyle = color;
  context.fillText(text, 0, 0);

  return context.canvas;
}

const getDirectionId = (directionOffset: number[]) => {
  // directionOffset coordinates are from { -1, 0, 1 }
  // mapping to range [0, 3)
  const x = Math.trunc(directionOffset[0] + 1);
  const y = Math.trunc(directionOffset[1] + 1);
  const z = Math.trunc(directionOffset[2] + 1);
  return 9 * z + 3 * y + x;
};

const getDirectionOffset = (directionId: number) => {
  const x = Math.trunc(directionId % 3) - 1;
  const y = Math.trunc((directionId / 3) % 3) - 1;
  const z = Math.trunc(directionId / 9) - 1;
  return [x, y, z];
};

const numberCubeFaces = 6;

const makeViewCubeGeometry = (cubeSize: number) => {
  const geometry = new BufferGeometry();

  // calculated in comments below
  const numberVertices = 336;
  const numberRectanglesPerCubeFace = 29;
  const numberIndices = numberRectanglesPerCubeFace * 2 * numberCubeFaces;

  const indices = new Uint16Array(numberIndices * 3);
  const vertices = new Float32Array(numberVertices * 3);
  const normals = new Float32Array(numberVertices * 3);
  const uvs = new Float32Array(numberVertices * 2);

  let verticesIndex = 0;

  const verticesMaps: Map<number, number>[] = [];
  for (let cubeFaceId = 0; cubeFaceId < numberCubeFaces; cubeFaceId += 1) {
    verticesMaps.push(new Map<number, number>());
  }

  const getCreateVertexIndex = (
    cubeFaceId: number,
    vertex: Float32Array,
    normal: Float32Array,
    uv: Float32Array
  ) => {
    // 0.1 comparison precission
    const coordinateScale = 10;
    // assuming coordinates are in range (-10, 10)
    const coordinateRange = 20 * coordinateScale;
    const x = Math.trunc(vertex[0] * coordinateScale + coordinateRange / 2);
    const y = Math.trunc(vertex[1] * coordinateScale + coordinateRange / 2);
    const z = Math.trunc(vertex[2] * coordinateScale + coordinateRange / 2);
    const vertexKey = coordinateRange * coordinateRange * z + coordinateRange * y + x;
    let vertexIndex = verticesMaps[cubeFaceId].get(vertexKey);
    if (vertexIndex !== undefined) {
      return vertexIndex;
    }
    vertexIndex = verticesIndex;

    verticesMaps[cubeFaceId].set(vertexKey, vertexIndex);
    vertices.set(vertex, 3 * vertexIndex);
    normals.set(normal, 3 * vertexIndex);
    uvs.set(uv, 2 * vertexIndex);

    verticesIndex += 1;

    return vertexIndex;
  };

  const materialMaps: Map<number, number[]>[] = [];
  for (let cubeFaceId = 0; cubeFaceId < numberCubeFaces; cubeFaceId += 1) {
    // all triangles (indexes)  per directionId
    materialMaps.push(new Map<number, number[]>());
  }

  const getCreateMaterialFaces = (cubeFaceId: number, offset: number[]) => {
    const directionId = getDirectionId(offset);
    let materialFaces = materialMaps[cubeFaceId].get(directionId);
    if (materialFaces !== undefined) {
      return materialFaces;
    }
    materialFaces = [];
    materialMaps[cubeFaceId].set(directionId, materialFaces);
    return materialFaces;
  };
  const getMaterialFaces = (cubeFaceId: number, directionId: number) => {
    return materialMaps[cubeFaceId].get(directionId);
  };

  const makeDirectionOffsets = () => {
    const result: number[][] = [];
    const grid = [-1, 0, 1];
    grid.forEach((x) => {
      grid.forEach((y) => {
        grid.forEach((z) => {
          result.push([x, y, z]);
        });
      });
    });
    return result;
  };
  // Each direction offset (cube corner, edge or face) corresponds to a material
  // Offset 0,0,0 used for other (non-directional) faces
  const directionOffsets = makeDirectionOffsets();

  // Highlighting groups of triangles (cube corners, edges, faces)
  // 0123456
  // AA-B-CC
  // AA---CC
  // --DDD--
  // E-DDD-F
  // --DDD--
  // GG---HH
  // GG-I-HH
  // 9 rectangles (A,B,C...) per cube face
  // 20 other rectangles ('-') per cube face
  // 8 vertices are unused (vertices are inside big rectangles) per cube face
  // 8*8 = 64 grid vertices per cube face
  // ^ cube faces have 6 * 64 - 6 * 8 (unused) = 336 vertices

  const rectangles = [
    { name: 'A', begin: [0, 0], end: [2, 2], offset: [-1, -1] },
    { name: 'B', begin: [3, 0], end: [4, 1], offset: [0, -1] },
    { name: 'C', begin: [5, 0], end: [7, 2], offset: [1, -1] },
    { name: 'D', begin: [2, 2], end: [5, 5], offset: [0, 0] },
    { name: 'E', begin: [0, 3], end: [1, 4], offset: [-1, 0] },
    { name: 'F', begin: [6, 3], end: [7, 4], offset: [1, 0] },
    { name: 'G', begin: [0, 5], end: [2, 7], offset: [-1, 1] },
    { name: 'I', begin: [3, 6], end: [4, 7], offset: [0, 1] },
    { name: 'H', begin: [5, 5], end: [7, 7], offset: [1, 1] },
  ];
  const faceLayout = ['AA-B-CC', 'AA---CC', '--DDD--', 'E-DDD-F', '--DDD--', 'GG---HH', 'GG-I-HH'];

  const vertex = new Float32Array(3);
  const normal = new Float32Array(3);
  const uv = new Float32Array(2);
  const currentOffset = [0, 0, 0];

  // const partSizes = [2, 1, 1, 6, 1, 1, 2];
  const gridCoordinates = [-7, -5, -4, -3, 3, 4, 5, 7];
  const numberFacesPerEdge = gridCoordinates.length - 1;
  const halfDepth = 7;
  const fullDepth = 2 * halfDepth;

  if (numberFacesPerEdge !== faceLayout.length) {
    throw new Error('Invalid cube layout');
  }

  // addCubeFace follows logic of buildPlane function from BoxGeometry
  // https://github.com/mrdoob/three.js/blob/master/src/geometries/BoxGeometry.js

  const addCubeFace = (
    u: number,
    v: number,
    w: number,
    udir: number,
    vdir: number,
    width: number,
    height: number,
    depth: number,
    cubeFaceId: number
  ) => {
    normal[u] = 0;
    normal[v] = 0;
    normal[w] = depth > 0 ? 1 : -1;

    const addRect = (begin: number[], end: number[], outFaces: number[]) => {
      const addVertex = (ix: number, iy: number) => {
        const gridX = gridCoordinates[ix];
        const gridY = gridCoordinates[iy];
        const x = gridX * width;
        const y = gridY * height;

        vertex[u] = x * udir;
        vertex[v] = y * vdir;
        vertex[w] = halfDepth * depth;

        uv[0] = (gridX + halfDepth) / fullDepth;
        uv[1] = 1 - (gridY + halfDepth) / fullDepth;

        return getCreateVertexIndex(cubeFaceId, vertex, normal, uv);
      };

      // a d
      // b c

      const a = addVertex(begin[0], begin[1]);
      const b = addVertex(begin[0], end[1]);
      const c = addVertex(end[0], end[1]);
      const d = addVertex(end[0], begin[1]);

      // adding 2 faces: (a,b,d), (b,c,d)
      outFaces.push(a, b, d, b, c, d);
    };

    rectangles.forEach(
      (group: { name: string; begin: number[]; end: number[]; offset: number[] }) => {
        const { begin, end, offset } = group;

        currentOffset[u] = offset[0] * udir;
        currentOffset[v] = offset[1] * vdir;
        currentOffset[w] = depth > 0 ? 1 : -1;
        const materialFaces = getCreateMaterialFaces(cubeFaceId, currentOffset);

        addRect(begin, end, materialFaces);
      }
    );

    // other (inactive) faces
    const materialFaces = getCreateMaterialFaces(cubeFaceId, [0, 0, 0]);
    for (let i = 0; i < numberFacesPerEdge; i += 1) {
      for (let j = 0; j < numberFacesPerEdge; j += 1) {
        if (faceLayout[j][i] === '-') {
          const begin = [i, j];
          const end = [i + 1, j + 1];
          addRect(begin, end, materialFaces);
        }
      }
    }
  };

  const width = 1;
  const height = 1;
  const depth = 1;

  const dimX = 0;
  const dimY = 1;
  const dimZ = 2;

  // 'Right', 'Left', 'Top', 'Bottom', 'Front', 'Back'

  addCubeFace(dimZ, dimY, dimX, -1, -1, depth, height, width, 0); // px
  addCubeFace(dimZ, dimY, dimX, 1, -1, depth, height, -width, 1); // nx
  addCubeFace(dimX, dimZ, dimY, 1, 1, width, depth, height, 2); // py
  addCubeFace(dimX, dimZ, dimY, 1, -1, width, depth, -height, 3); // ny
  addCubeFace(dimX, dimY, dimZ, 1, -1, width, height, depth, 4); // pz
  addCubeFace(dimX, dimY, dimZ, -1, -1, width, height, -depth, 5); // nz

  const faceToMaterialIndexArray: number[] = [];

  const materialArray: {
    directionId: number;
    cubeFaceId: number;
  }[] = [];

  let indicesIndex = 0;

  for (let cubeFaceId = 0; cubeFaceId < numberCubeFaces; cubeFaceId += 1) {
    for (const directionOffset of directionOffsets) {
      const directionId = getDirectionId(directionOffset);
      const materialFaces = getMaterialFaces(cubeFaceId, directionId);
      if (materialFaces !== undefined) {
        const groupCount = materialFaces.length;
        if (groupCount !== 0) {
          const materialIndex = materialArray.length;

          indices.set(materialFaces, indicesIndex);
          const facesCount = Math.trunc(groupCount / 3);
          for (let index = 0; index < facesCount; index += 1) {
            faceToMaterialIndexArray.push(materialIndex);
          }
          geometry.addGroup(indicesIndex, groupCount, materialIndex);
          indicesIndex += groupCount;
          materialArray.push({ directionId, cubeFaceId });
        }
      }
    }
  }

  for (let index = 0; index < vertices.length; index += 1) {
    vertices[index] *= cubeSize / fullDepth;
  }

  geometry.setIndex(new THREE.Uint16BufferAttribute(indices, 1));
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));

  return { viewCubeGeometry: geometry, faceToMaterialIndexArray, materialArray };
};

const lightPosition: [x: number, y: number, z: number] = [5, 5, 5];

export const ViewCube: FC<ViewCubeExtendedProps> = ({
  cubeSize,
  fontSize,
  colors,
  compass,
  northRotation,
  moveCameraToViewDirection,
  position: propsPosition,
}) => {
  const camera = useThree((state) => state.camera);
  const gl = useThree((state) => state.gl);
  const size = useThree((state) => state.size);

  const cubeGroup = useRef<THREE.Group | null>(null);
  const [hoverDirectionId, setHoverDirectionId] = useState<number>();
  const cubePadding = useMemo(() => Math.trunc(cubeSize * 0.5), [cubeSize]);

  const cubeCamera = useMemo(() => new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 10), []);
  const cubeScene = useMemo(() => new Scene(), []);
  const matrix = useMemo(() => new Matrix4(), []);

  useEffect(() => {
    cubeCamera.left = -size.width / 2;
    cubeCamera.right = size.width / 2;
    cubeCamera.top = size.height / 2;
    cubeCamera.bottom = -size.height / 2;
    cubeCamera.near = -cubeSize * 100;
    cubeCamera.far = cubeSize * 100;
    cubeCamera.position.set(0, 0, 2 * cubeSize);
    cubeCamera.updateProjectionMatrix();
  }, [cubeCamera, cubeSize, size]);

  const initialRotation = useMemo(() => {
    const initialQuaternion = new THREE.Quaternion();
    const euler = new THREE.Euler(Math.PI / 2, 0, 0);
    initialQuaternion.setFromEuler(euler);
    return initialQuaternion;
  }, []);

  useEffect(() => {
    return addAfterEffect(() => {
      if (cubeCamera && cubeGroup.current) {
        matrix.copy(camera.matrix).invert();

        cubeGroup.current.quaternion.setFromRotationMatrix(matrix).multiply(initialRotation);
        gl.autoClear = false;
        gl.clearDepth();
        gl.render(cubeScene, cubeCamera);
        gl.autoClear = true;
      }
    });
  }, [cubeCamera, matrix, camera.matrix, initialRotation, gl, cubeScene]);

  useEffect(() => {
    cubeGroup.current?.traverse((object: Object3D) => {
      object.layers.enable(ViewerLayer.AutoPickable);
    });
    cubeGroup.current?.layers.enable(ViewerLayer.AutoPickable);

    cubeCamera.layers.enable(ViewerLayer.AutoPickable);
    if (cubeCamera !== undefined) {
      cubeCamera.lookAt(0, 0, 0);
      cubeCamera.updateProjectionMatrix();
    }
  }, [cubeCamera]);

  const { viewCubeGeometry, materialArray, faceToMaterialIndexArray } = useMemo(() => {
    return makeViewCubeGeometry(cubeSize);
  }, [cubeSize]);

  const updateHoverDirectionId = useCallback(
    (faceIndex: number | undefined) => {
      if (faceIndex === undefined) {
        setHoverDirectionId(undefined);
        return;
      }
      const zeroDirectionId = getDirectionId([0, 0, 0]);
      const materialIndex = faceToMaterialIndexArray[faceIndex];
      const { directionId } = materialArray[materialIndex];
      setHoverDirectionId(directionId !== zeroDirectionId ? directionId : undefined);
    },
    [faceToMaterialIndexArray, materialArray]
  );

  const cubeFaceMaterials = useMemo(() => {
    const { foreground, background, hoverForeground, hoverBackground } = colors;

    const faceCanvasParameters = calcFaceCanvasParameters(cubeSize, 'BOTTOM', fontSize);

    const makeFaceMaterial = (text: string, fgColor: string, bgColor: string) => {
      const canvas = makeFaceCanvas(faceCanvasParameters, text.toUpperCase(), fgColor, bgColor);
      const texture = new CanvasTexture(
        canvas
        // THREE.UVMapping,
        // THREE.ClampToEdgeWrapping,
        // THREE.ClampToEdgeWrapping
        // THREE.LinearFilter
        // THREE.LinearMipmapNearestFilter
      );
      // texture.colorSpace = THREE.SRGBColorSpace;
      // texture.colorSpace = THREE.LinearSRGBColorSpace;
      texture.colorSpace = THREE.NoColorSpace;
      texture.generateMipmaps = true;
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;

      const material = new MeshLambertMaterial({
        map: texture,
        // opacity: 0.9,
        // transparent: true,
        side: THREE.FrontSide,
      });
      return material;
    };
    return ['Right', 'Left', 'Top', 'Bottom', 'Front', 'Back'].map((text) => {
      return [
        makeFaceMaterial(text, foreground, background),
        makeFaceMaterial(text, hoverForeground, hoverBackground),
      ];
    });
  }, [cubeSize, fontSize, colors]);

  const materials = useMemo(() => {
    return materialArray.map((materialInfo: { cubeFaceId: number; directionId: number }) => {
      const { cubeFaceId, directionId } = materialInfo;
      return cubeFaceMaterials[cubeFaceId][directionId === hoverDirectionId ? 1 : 0];
    });
  }, [cubeFaceMaterials, materialArray, hoverDirectionId]);

  const positionMemo = useMemo(
    () =>
      propsPosition ||
      ([size.width / 2 - cubeSize - cubePadding, size.height / 2 - cubeSize - cubePadding, 0] as [
        x: number,
        y: number,
        z: number,
      ]),
    [size, cubeSize, cubePadding, propsPosition]
  );

  const onPointerDown = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      updateHoverDirectionId(removeNullTyping(event.faceIndex));

      if (hoverDirectionId !== undefined) {
        const direction = getDirectionOffset(hoverDirectionId);
        // compensate initial rotation
        const adjustedDirection = new THREE.Vector3(...direction);
        adjustedDirection.applyQuaternion(initialRotation);
        moveCameraToViewDirection([adjustedDirection.x, adjustedDirection.y, adjustedDirection.z]);
        event.stopPropagation();
      }
    },
    [updateHoverDirectionId, hoverDirectionId, initialRotation, moveCameraToViewDirection]
  );

  const onPointerOut = useCallback(
    () => updateHoverDirectionId(undefined),
    [updateHoverDirectionId]
  );

  const onPointerMove = useCallback(
    (event: ThreeEvent<PointerEvent>) => updateHoverDirectionId(removeNullTyping(event.faceIndex)),
    [updateHoverDirectionId]
  );

  return createPortal(
    <>
      <group key="view_cube_group" ref={cubeGroup} position={positionMemo}>
        <mesh
          key="view_cube_mesh"
          raycast={useCamera(cubeCamera)}
          geometry={viewCubeGeometry}
          material={materials}
          onPointerDown={onPointerDown}
          onPointerOut={onPointerOut}
          onPointerMove={onPointerMove}
        />

        <ViewCubeCompass
          {...(compass || defaultViewCubeProps.compass)}
          cubeSize={cubeSize}
          northRotation={northRotation}
        />
      </group>

      <ambientLight intensity={1} />
      <pointLight position={lightPosition} intensity={0.5} decay={0} />
    </>,
    cubeScene
  ); // as ReactElement<any, any>;
};
